import { useCallback, useState } from "react";
import { type NoticeStatus, type NoticeSource, type NoticeChannel } from "@/hooks/use-notices";

/**
 * The in-page tabs of the `/notices` surface. Three read the LETTER record;
 * Issues reads the sweep's open worklist, Scheduled reads each subject's
 * POSITION in its ladder, Templates the template-family library and Rules the
 * escalation ladders themselves. All of them switch the panel under one tab
 * strip — none leaves the route.
 */
export type NoticesTab = "queue" | "issues" | "scheduled" | "sent" | "all" | "templates" | "rules";

/**
 * The tabs whose panel queries the letter list. Issues, Scheduled, Templates
 * and Rules are excluded: none has a status set or a letter filter bar, and
 * every helper below would have to invent an answer for them.
 */
export type LetterTab = "queue" | "sent" | "all";

export const NOTICES_TABS: readonly NoticesTab[] = [
  "queue",
  "issues",
  "scheduled",
  "sent",
  "all",
  "templates",
  "rules",
];

export const LETTER_TABS: readonly LetterTab[] = ["queue", "sent", "all"];

export const DEFAULT_NOTICES_TAB: NoticesTab = "queue";

/**
 * The canonical address of the template library. It is a TAB of `/notices`, not
 * a page of its own, so every "back to the library" affordance names it once
 * from here rather than hard-coding a path that a tab rename would silently
 * break.
 */
export const TEMPLATES_TAB_NAV = { to: "/notices", search: { tab: "templates" } } as const;

/** The same, for the escalation-ladder index the builder pages return to. */
export const RULES_TAB_NAV = { to: "/notices", search: { tab: "rules" } } as const;

/**
 * Narrow a tab to the letter half. An ALLOW-LIST, deliberately: the subtractive
 * form it replaced ("anything but Templates") classified every tab added after
 * it as a letter tab, which would have routed the ladder panels into the letters
 * panel and fired letter reads for a surface that lists no letters.
 */
export function isLetterTab(tab: NoticesTab): tab is LetterTab {
  return (LETTER_TABS as readonly string[]).includes(tab);
}

/**
 * The review queue is every UNSENT letter: a draft still being written and an
 * approved letter still waiting for its dispatch are the same worklist.
 */
const QUEUE_STATUSES: readonly NoticeStatus[] = ["draft", "approved"];

/**
 * The dispatched set — a letter that has left the building — named identically
 * here and in the records projection, the RLS grant and the customer wire.
 *
 * `print_ready` is a member: a shut-off letter is printed for hand delivery and
 * never touches a carrier, so its dispatch is the print, not a send. `sending`
 * is a member too, and it is the one a UI is tempted to drop: a letter in
 * flight has been dispatched, and `sending` is a state a letter can get STUCK
 * in, so leaving it out put it on no working tab at all.
 *
 * `imported` is deliberately NOT a member: a letter the org importer carried
 * over was dispatched by a previous system, not this one, and the records
 * projection, the RLS grant and the customer wire all leave it out of the
 * dispatched set for the same reason. It is history, read on All notices.
 */
const SENT_STATUSES: readonly NoticeStatus[] = [
  "sending",
  "sent",
  "delivered",
  "print_ready",
  "hand_delivered",
];

/** Unknown / absent `?tab=` values fall back to the queue. */
export function parseNoticesTab(value: unknown): NoticesTab {
  if (typeof value !== "string") return DEFAULT_NOTICES_TAB;
  return NOTICES_TABS.find((tab) => tab === value) ?? DEFAULT_NOTICES_TAB;
}

/**
 * The `status` array a tab asks the list endpoint for. The All notices tab
 * sends none, so cancelled / rejected / failed letters are included — that tab
 * carries its own Status select instead.
 */
export function statusesForTab(tab: LetterTab): NoticeStatus[] | undefined {
  switch (tab) {
    case "queue":
      return [...QUEUE_STATUSES];
    case "sent":
      return [...SENT_STATUSES];
    default:
      return undefined;
  }
}

/**
 * Whether a tab's list folds a letter that goes by email with a print copy to
 * its emailed row (`collapse_pairs`). Only the review queue under its own
 * status set does: before dispatch the two rows agree, so each letter is one
 * card. A drill status replaces that set and may name a status only the print
 * copy holds, which a list judged by the emailed row would never show, so a
 * drilled queue lists both rows, and the record tabs always do.
 */
export function collapsesPairs(
  tab: LetterTab,
  drillStatus: readonly NoticeStatus[] | undefined,
): boolean {
  return tab === "queue" && drillStatus === undefined;
}

/** One tab's filter-bar state. Every field is a list-query axis. */
export interface NoticeFilterState {
  q?: string;
  source?: NoticeSource;
  channel?: NoticeChannel;
  /** Letters one escalation ladder produced. */
  ladder_id?: string;
  /** Letters drafted for one STEP of that ladder — meaningless without it. */
  ladder_step_id?: string;
  /** All-notices tab only — the other tabs pin `status` from the tab itself. */
  status?: NoticeStatus;
  /** ISO date (yyyy-mm-dd) — generated_at lower bound. */
  from?: string;
  /** ISO date (yyyy-mm-dd) — generated_at upper bound. */
  to?: string;
}

const EMPTY_FILTERS: NoticeFilterState = {};

type FiltersByTab = Record<LetterTab, NoticeFilterState>;

export interface UseNoticeTabFiltersReturn {
  filtersFor: (tab: LetterTab) => NoticeFilterState;
  setFilter: <K extends keyof NoticeFilterState>(
    tab: LetterTab,
    key: K,
    value: NoticeFilterState[K],
  ) => void;
  clearFilter: (tab: LetterTab, key: keyof NoticeFilterState) => void;
  resetFilters: (tab: LetterTab) => void;
}

/**
 * Per-tab filter state for the `/notices` tab strip. Each tab answers a
 * different question — the queue triages unsent work, Sent audits what left,
 * All notices searches the whole record — so a channel or date window set on
 * one tab must survive a trip to another and back rather than being reset or
 * silently re-applied somewhere it was never asked for.
 *
 * State is in-memory only (a tab switch is a within-session move); the tab
 * itself lives in the URL so a drill-down link still lands on the right one.
 * It is held by the ROUTE shell rather than the letters panel, so a detour
 * through Templates — which unmounts that panel — does not erase it.
 */
export function useNoticeTabFilters(): UseNoticeTabFiltersReturn {
  const [byTab, setByTab] = useState<FiltersByTab>(() => ({
    queue: { ...EMPTY_FILTERS },
    sent: { ...EMPTY_FILTERS },
    all: { ...EMPTY_FILTERS },
  }));

  const filtersFor = useCallback((tab: LetterTab) => byTab[tab], [byTab]);

  const setFilter = useCallback(
    <K extends keyof NoticeFilterState>(tab: LetterTab, key: K, value: NoticeFilterState[K]) => {
      setByTab((prev) => ({ ...prev, [tab]: { ...prev[tab], [key]: value } }));
    },
    [],
  );

  const clearFilter = useCallback((tab: LetterTab, key: keyof NoticeFilterState) => {
    setByTab((prev) => ({ ...prev, [tab]: { ...prev[tab], [key]: undefined } }));
  }, []);

  const resetFilters = useCallback((tab: LetterTab) => {
    setByTab((prev) => ({ ...prev, [tab]: { ...EMPTY_FILTERS } }));
  }, []);

  return { filtersFor, setFilter, clearFilter, resetFilters };
}
